import React, { useState } from 'react';
import type { SlaClockKey, SlaPriorityMatch, SlaTargetRow, TicketStatusCategory } from '@troia-v3/types';

import { Button, Input, MultiSelect, Select, Switch, Tooltip } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import {
  applyDurationFieldUnitChange, applyDurationFieldValueChange,
  CLOCK_KEY_LABELS, PRIORITY_MATCH_LABELS, STATUS_CATEGORY_OPTIONS,
  msToDuration, msToUnitValue, validateTargetRow,
  type DurationFieldState, type DurationUnit, type SlaPolicyDraft,
} from '../utils/slaPolicyHelpers';

export interface SlaTargetsTabProps {
  draft: SlaPolicyDraft;
  onChange: (next: SlaPolicyDraft) => void;
  /**
   * Identidade da política/rascunho sendo editado (ex.: `policy?.id ?? 'new'`
   * em `SlaPolicyDrawer`). Vira PARTE da `key` de cada linha — trocar de
   * política no rail força REMOUNT das linhas (e portanto reinicializa o
   * estado local do campo de prazo a partir do `targetMs` da política nova)
   * em vez de deixar o rascunho de uma política vazar visualmente pra outra
   * que abra depois com a mesma posição de índice. Opcional: sem ela, o
   * comportamento é o de antes (chave só por conteúdo+índice) — aceitável
   * quando o chamador não tem múltiplos rascunhos possíveis.
   */
  resetKey?: string;
}

const CLOCK_OPTIONS = (Object.keys(CLOCK_KEY_LABELS) as SlaClockKey[])
  .map((value) => ({ value, label: CLOCK_KEY_LABELS[value] }));

const PRIORITY_OPTIONS = (Object.keys(PRIORITY_MATCH_LABELS) as SlaPriorityMatch[])
  .map((value) => ({ value, label: PRIORITY_MATCH_LABELS[value] }));

const UNIT_OPTIONS: Array<{ value: DurationUnit; label: string }> = [
  { value: 'minutes', label: 'minutos' },
  { value: 'hours', label: 'horas' },
  { value: 'days', label: 'dias' },
];

interface SlaTargetDurationFieldProps {
  /** `targetMs` do rascunho PAI — usado SÓ para inicializar o estado local
   *  (mount) e para o "reverter ao último válido" do blur. NUNCA lido de
   *  novo pra recomputar a unidade a cada render — ver o porquê no JSDoc de
   *  `DurationFieldState` em `slaPolicyHelpers.ts`. */
  targetMs: number;
  /** Sobe pro rascunho pai só quando o campo tem um número válido (>0). */
  onCommit: (targetMs: number) => void;
  valueTestId: string;
  unitTestId: string;
}

/**
 * Campo "Prazo de [valor] [unidade]" com estado PRÓPRIO
 * (`DurationFieldState`) — a unidade é escolha do usuário, NUNCA recomputada
 * de `targetMs` a cada render. Achado de duas rodadas de revisão (Rodada 1:
 * apagar o campo; Rodada 2: digitar "0" explícito) — os dois flipavam a
 * unidade sozinha porque a versão antiga derivava `{value, unit}` de
 * `msToDuration(row.targetMs)` a cada render, e QUALQUER valor que passasse
 * por 0 contaminava o próximo commit. Aqui `unit` só muda pela mão do
 * usuário no Select (`applyDurationFieldUnitChange`), e um valor não-positivo
 * (`''`, `'0'`, negativo) nunca é commitado pro pai (`applyDurationFieldValueChange`
 * retorna `commitMs: null`) — o `targetMs` do pai simplesmente NUNCA passa
 * por 0 no meio da digitação, e portanto não há gatilho para o flip.
 *
 * O reset (reinicializar a partir de um `targetMs` NOVO — outra política, ou
 * uma linha recém-criada) acontece por REMOUNT: o chamador (`SlaTargetsTab`)
 * inclui `resetKey` na `key` da linha.
 */
const SlaTargetDurationField: React.FC<SlaTargetDurationFieldProps> = ({
  targetMs, onCommit, valueTestId, unitTestId,
}) => {
  const [state, setState] = useState<DurationFieldState>(() => {
    const initial = msToDuration(targetMs);
    return { rawValue: String(initial.value), unit: initial.unit };
  });

  const handleValueChange = (raw: string) => {
    const { next, commitMs } = applyDurationFieldValueChange(state, raw);
    setState(next);
    if (commitMs !== null) onCommit(commitMs);
  };

  const handleUnitChange = (unit: DurationUnit) => {
    const { next, commitMs } = applyDurationFieldUnitChange(state, unit);
    setState(next);
    if (commitMs !== null) onCommit(commitMs);
  };

  const handleBlur = () => {
    const numeric = Number(state.rawValue);
    if (state.rawValue !== '' && Number.isFinite(numeric) && numeric > 0) return; // já válido
    // Campo abandonado num estado inválido (vazio, "0", etc.) — volta a
    // mostrar o último valor de fato salvo no rascunho pai, na unidade
    // CORRENTE (`msToUnitValue`, não `msToDuration`): usar o best-fit do
    // `msToDuration` aqui reintroduziria o mesmo tipo de flip por outra
    // porta — 86400000ms vira "1 dia" pelo best-fit, mas o usuário podia
    // estar editando em "horas", e o revert mostraria "1 horas" (≠ 24h).
    setState((prev) => ({ ...prev, rawValue: String(msToUnitValue(targetMs, prev.unit)) }));
  };

  return (
    <>
      <Input
        type="number"
        min={1}
        value={state.rawValue}
        onChange={(e) => handleValueChange(e.target.value)}
        onBlur={handleBlur}
        fullWidth={false}
        style={{ width: 84, textAlign: 'center' }}
        data-testid={valueTestId}
      />
      <Select
        value={state.unit}
        onChange={(value) => handleUnitChange(value as DurationUnit)}
        options={UNIT_OPTIONS}
        fullWidth={false}
        data-testid={unitTestId}
      />
    </>
  );
};

SlaTargetDurationField.displayName = 'SlaTargetDurationField';

/**
 * Aba "Alvos" — as linhas (relógio × prioridade) da política.
 *
 * `'*'` é a linha coringa: casa qualquer prioridade que não tenha linha
 * própria. Uma política sem coringa e sem linha para a prioridade do item
 * simplesmente NÃO define aquele relógio, e a busca continua nas políticas
 * seguintes da cascata — não é erro, é o fallback do estágio 2.
 */
export const SlaTargetsTab: React.FC<SlaTargetsTabProps> = ({ draft, onChange, resetKey }) => {
  const isConversation = draft.entityType === 'conversation';

  const patchRow = (index: number, patch: Partial<SlaTargetRow>) => {
    const targets = draft.targets.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange({ ...draft, targets });
  };

  const addRow = () =>
    onChange({
      ...draft,
      targets: [
        ...draft.targets,
        {
          clockKey: 'resolution',
          priority: '*',
          targetMs: 24 * 3_600_000,
          useBusinessHours: true,
          pausesOn: [],
        },
      ],
    });

  const removeRow = (index: number) =>
    onChange({ ...draft, targets: draft.targets.filter((_, i) => i !== index) });

  return (
    <div className="space-y-4" data-testid="sla-policy-targets-tab">
      <p className="text-xs leading-relaxed" style={{ color: '#64748b' }}>
        Cada linha define o prazo de um relógio para uma prioridade.{' '}
        <strong>{PRIORITY_MATCH_LABELS['*']}</strong> é a linha coringa — vale para toda prioridade
        que não tiver linha própria.
      </p>

      {draft.targets.map((row, index) => {
        const error = validateTargetRow(row, draft.entityType);
        return (
          <div
            // A `key` NÃO pode incluir conteúdo editável da linha
            // (`clockKey`/`priority`): trocar o Select de Relógio ou de
            // Prioridade mudaria a key, remontando `SlaTargetDurationField` —
            // que reinicializa a unidade por `msToDuration(targetMs)`
            // (best-fit) e faria "24 horas" reaparecer como "1 dias". É o
            // MESMO flip das rodadas 1/2, por uma porta mais estreita (só a
            // exibição; o valor gravado continua certo). Identidade da linha =
            // política (`resetKey`) + posição. Resíduo conhecido: remover uma
            // linha desloca os índices seguintes e as remonta — ação explícita
            // e rara, ao contrário de trocar um Select.
            key={`${resetKey ?? ''}-row-${index}`}
            className="p-4 rounded-xl"
            style={{ backgroundColor: '#fafafa', border: '1px solid #e8e8ec' }}
            data-testid={`sla-policy-target-row-${index}`}
          >
            <div className={`grid ${isConversation ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
              <Select
                label="Relógio"
                value={row.clockKey}
                onChange={(value) => patchRow(index, { clockKey: value as SlaClockKey })}
                options={CLOCK_OPTIONS}
                data-testid={`sla-policy-target-clock-${index}`}
              />
              <Select
                label="Prioridade"
                value={row.priority}
                onChange={(value) => patchRow(index, { priority: value as SlaPriorityMatch })}
                options={PRIORITY_OPTIONS}
                data-testid={`sla-policy-target-priority-${index}`}
              />
              {!isConversation && (
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                    Pausa nestes status
                  </label>
                  <MultiSelect
                    options={STATUS_CATEGORY_OPTIONS}
                    selectedValues={row.pausesOn}
                    onChange={(values) => patchRow(index, { pausesOn: values as TicketStatusCategory[] })}
                    placeholder="Nunca pausa"
                    data-testid={`sla-policy-target-pauses-${index}`}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-end gap-4 mt-4">
              <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
                Prazo de
                <SlaTargetDurationField
                  targetMs={row.targetMs}
                  onCommit={(targetMs) => patchRow(index, { targetMs })}
                  valueTestId={`sla-policy-target-value-${index}`}
                  unitTestId={`sla-policy-target-unit-${index}`}
                />
              </label>

              <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
                <Switch
                  checked={row.useBusinessHours}
                  onChange={(checked) => patchRow(index, { useBusinessHours: checked })}
                  data-testid={`sla-policy-target-business-hours-${index}`}
                />
                Contar só horário comercial
              </label>

              <span className="flex-1" />

              {draft.targets.length > 1 && (
                <Tooltip content="Remover esta linha">
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="p-1.5 rounded"
                    style={{ color: '#ef4444' }}
                    data-testid={`sla-policy-target-remove-${index}`}
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </Tooltip>
              )}
            </div>

            {error && (
              <p className="text-xs mt-2" style={{ color: '#dc2626' }} data-testid={`sla-policy-target-error-${index}`}>
                {error}
              </p>
            )}
          </div>
        );
      })}

      {/* A pausa por status NÃO existe em conversa — e o motivo fica na tela,
          não só no código: `status: 'waiting'` de conversa significa "na fila,
          o cliente esperando por nós", o OPOSTO do `waiting` de chamado. */}
      {isConversation && (
        <p className="text-xs" style={{ color: '#64748b' }} data-testid="sla-policy-targets-conversation-note">
          Política de conversa não pausa por status: o relógio pausa sozinho enquanto nós
          falamos por último e retoma quando o cliente responde.
        </p>
      )}

      <Button variant="secondary" icon="plus" onClick={addRow} data-testid="sla-policy-target-add">
        Adicionar linha de alvo
      </Button>
    </div>
  );
};

SlaTargetsTab.displayName = 'SlaTargetsTab';
